import { Component, ElementRef, computed, effect, inject, input, untracked } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ContentStateService } from '../../content-state.service';
import { AudioChunkDisplayerComponent } from './displayers/audio-chunk-displayer.component';
import { CodeChunkDisplayerComponent } from './displayers/code-chunk-displayer.component';
import { FileChunkDisplayerComponent } from './displayers/file-chunk-displayer.component';
import { HtmlChunkDisplayerComponent } from './displayers/html-chunk-displayer.component';
import { ImageChunkDisplayerComponent } from './displayers/image-chunk-displayer.component';
import { LatexChunkDisplayerComponent } from './displayers/latex-chunk-displayer.component';
import { QuizChunkDisplayerComponent } from './displayers/quiz-chunk-displayer.component';
import { TextChunkDisplayerComponent } from './displayers/text-chunk-displayer.component';
import { TableChunkDisplayerComponent } from './displayers/table-chunk-displayer.component';
import { TranscriptChunkDisplayerComponent } from './displayers/transcript-chunk-displayer.component';
import { VideoChunkDisplayerComponent } from './displayers/video-chunk-displayer.component';
import { WhiteboardChunkDisplayerComponent } from './displayers/whiteboard-chunk-displayer.component';

@Component({
  selector: 'app-chunk-displayer',
  standalone: true,
  imports: [
    TextChunkDisplayerComponent,
    HtmlChunkDisplayerComponent,
    CodeChunkDisplayerComponent,
    ImageChunkDisplayerComponent,
    VideoChunkDisplayerComponent,
    FileChunkDisplayerComponent,
    AudioChunkDisplayerComponent,
    QuizChunkDisplayerComponent,
    LatexChunkDisplayerComponent,
    TranscriptChunkDisplayerComponent,
    TableChunkDisplayerComponent,
    WhiteboardChunkDisplayerComponent,
  ],
  template: `
    @if (chunk()?.mainEdition) {
      <div (dragenter)="chunk().editMode = 'addAudio'">
        <!-- Error Indicator -->
        @if (chunk().error) {
          <div class="dot" [class.dot-in-box]="highlightedChunk() === chunk()._id">
            <span></span>
          </div>
        }

        <!-- Dispatcher to the correct component based on chunk type -->
        @switch (chunk().type) {
          @case ('text') {
            <div [class.is-playing]="isPlaying()">
              <app-text-chunk-displayer [chunk]="chunk()" />
            </div>
          }
          @case ('latex') {
            <app-latex-chunk-displayer [chunk]="chunk()" />
          }
          @case ('html') {
            <div [class.is-playing]="isPlaying()">
              <app-html-chunk-displayer [chunk]="chunk()" />
            </div>
          }
          @case ('code') {
            <app-code-chunk-displayer [chunk]="chunk()" />
          }
          @case ('image') {
            <app-image-chunk-displayer [chunk]="chunk()" />
          }
          @case ('video') {
            <app-video-chunk-displayer [chunk]="chunk()" />
          }
          @case ('file') {
            <app-file-chunk-displayer [chunk]="chunk()" />
          }
          @case ('audio') {
            <app-audio-chunk-displayer [chunk]="chunk()" />
          }
          @case ('quiz') {
            <app-quiz-chunk-displayer [chunk]="chunk()" />
          }
          @case ('canvas-transcript') {
            <app-transcript-chunk-displayer [chunk]="chunk()" />
          }
          @case ('table') {
            <app-table-chunk-displayer [chunk]="chunk()" />
          }
          @case ('whiteboard') {
            <app-whiteboard-chunk-displayer [chunk]="chunk()" />
          }
        }
      </div>
    }
  `,
  styleUrls: ['./chunk-displayer.component.scss'],
})
export class ChunkDisplayerComponent {
  private contentState = inject(ContentStateService) as any;
  private elementRef = inject(ElementRef);
  private translate = inject(TranslateService);

  chunk = input.required<any>();
  highlightedChunk = input<string>();

  private isTokenized = false;
  private tokenSpans: HTMLElement[] = [];
  private lastActiveIndex = -1;
  private lastChunkId = '';
  private lastLanguage = '';

  public isPlaying = computed(() => {
    const playerState = this.contentState.playerState();
    // Simplified for testing: active if this chunk is highlighted, and the player is not stopped
    return this.highlightedChunk() === this.chunk()?._id && playerState.status !== 'stopped';
  });

  constructor() {
    effect(() => {
      const chunk = this.chunk();
      const playing = this.isPlaying();
      const currentTime = this.contentState.playerCurrentTime();
      const currentLang = this.translate.currentLang;

      if (chunk?._id !== this.lastChunkId || currentLang !== this.lastLanguage) {
        untracked(() => {
          this.clearHighlighting();
          this.lastChunkId = chunk?._id || '';
          this.lastLanguage = currentLang || '';
        });
      }

      if (playing) {
        untracked(() => {
          this.highlightWordAtTime(currentTime);
        });
      } else {
        untracked(() => {
          this.clearHighlighting();
        });
      }
    });
  }

  private tokenizeContent() {
    if (this.isTokenized) return;

    let container = this.elementRef.nativeElement;
    if (!container) return;

    // Strict scoping: Only tokenize actual content containers to avoid highlighting titles, buttons, etc.
    const previewContainer = container.querySelector('.transcript-preview-container, app-text-chunk-displayer, app-html-chunk-displayer, app-latex-chunk-displayer');
    if (previewContainer) {
      container = previewContainer;
    } else {
      // If none of the specific content containers exist, do not tokenize at all.
      // This prevents mistakenly tokenizing header actions, titles, icons, or players.
      return;
    }

    this.tokenSpans = [];
    let wordIndex = 0;
    const traverse = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const parent = node.parentNode;
        if (!parent) return;

        const parentTagName = (parent as HTMLElement).tagName?.toLowerCase();
        if (['script', 'style', 'code', 'noscript'].includes(parentTagName)) {
          return;
        }
        if ((parent as HTMLElement).closest('.ql-formula') || (parent as HTMLElement).closest('.katex')) {
          return;
        }

        const text = node.nodeValue || '';
        if (!text.trim()) return; // Skip empty text nodes

        const regex = /([a-zA-Z0-9À-ÿ\u00C0-\u017F'-]+)/g;
        let match;
        const fragments: Node[] = [];
        let lastIndex = 0;
        let hasMatches = false;

        while ((match = regex.exec(text)) !== null) {
          hasMatches = true;
          const matchIndex = match.index;
          const matchedWord = match[0];

          if (matchIndex > lastIndex) {
            fragments.push(document.createTextNode(text.substring(lastIndex, matchIndex)));
          }

          const span = document.createElement('span');
          span.className = 'word-token';
          span.setAttribute('data-word-index', String(wordIndex));
          span.textContent = matchedWord;
          fragments.push(span);
          this.tokenSpans.push(span);
          wordIndex++;

          lastIndex = regex.lastIndex;
        }

        if (hasMatches) {
          if (lastIndex < text.length) {
            fragments.push(document.createTextNode(text.substring(lastIndex)));
          }

          if (fragments.length > 0) {
            const fragment = document.createDocumentFragment();
            fragments.forEach((f) => fragment.appendChild(f));
            parent.replaceChild(fragment, node);
          }
        }
      } else {
        const children = Array.from(node.childNodes);
        for (const child of children) {
          traverse(child);
        }
      }
    };

    traverse(container);
    this.isTokenized = true;
  }

  private highlightWordAtTime(currentTime: number) {
    let container = this.elementRef.nativeElement;
    if (!container) {
      return;
    }

    const previewContainer = container.querySelector('.transcript-preview-container');
    if (previewContainer) {
      container = previewContainer;
    }

    const existingSpans = container.querySelectorAll('.word-token');

    // If spans exist in DOM but our array is empty (e.g. after clearHighlighting), just re-collect them
    if (existingSpans.length > 0 && this.tokenSpans.length === 0) {
      this.tokenSpans = Array.from(existingSpans) as HTMLElement[];
      this.isTokenized = true;
    }
    // If no spans exist in the DOM, we must tokenize
    else if (existingSpans.length === 0) {
      this.isTokenized = false;
      this.tokenSpans = [];
      this.tokenizeContent();
    }

    if (this.tokenSpans.length === 0) {
      return;
    }

    const currentLang = this.translate.currentLang;
    const currentAudio = this.chunk()?.audios?.find(
      (a: any) => a._id === currentLang || a.id === currentLang || a.filename?.includes(`_${currentLang}.`) || a.fileLink?.includes(`_${currentLang}.`)
    );
    const words = currentAudio?.words || [];

    let activeIndex = -1;
    if (words && words.length > 0) {
      const padding = 0.1; // 100ms padding to account for lag
      for (let i = words.length - 1; i >= 0; i--) {
        if (words[i].startTime <= currentTime + padding) {
          activeIndex = i;
          break;
        }
      }
    }

    // Ensure we don't go out of bounds if words array is larger than tokenized spans
    if (activeIndex >= this.tokenSpans.length) {
      activeIndex = this.tokenSpans.length - 1;
    }

    if (activeIndex === this.lastActiveIndex) {
      return;
    }

    if (this.lastActiveIndex !== -1 && this.tokenSpans[this.lastActiveIndex]) {
      this.tokenSpans[this.lastActiveIndex].classList.remove('highlighted');
    }

    if (activeIndex !== -1 && this.tokenSpans[activeIndex]) {
      const activeSpan = this.tokenSpans[activeIndex];
      activeSpan.classList.add('highlighted');
      this.scrollToActiveWord(activeSpan);
    }

    this.lastActiveIndex = activeIndex;
  }

  private scrollToActiveWord(span: HTMLElement): void {
    let parent = span.parentElement;
    while (parent && parent !== this.elementRef.nativeElement) {
      const style = getComputedStyle(parent);
      if ((style.overflowY === 'auto' || style.overflowY === 'scroll') && parent.scrollHeight > parent.clientHeight) {
        const parentRect = parent.getBoundingClientRect();
        const spanRect = span.getBoundingClientRect();

        // Relative top position of the active word inside the visible container
        const relativeTop = spanRect.top - parentRect.top;

        // Define boundaries: 15% from top, 70% from top
        const topThreshold = parent.clientHeight * 0.15;
        const bottomThreshold = parent.clientHeight * 0.7;

        // If the word crosses the boundaries, smoothly scroll it back to the exact center
        if (relativeTop > bottomThreshold || relativeTop < topThreshold) {
          const targetRelativeTop = parent.clientHeight / 2 - spanRect.height / 2;
          const offset = relativeTop - targetRelativeTop;

          parent.scrollBy({ top: offset, behavior: 'smooth' });
        }

        return;
      }
      parent = parent.parentElement;
    }
  }

  private clearHighlighting() {
    if (this.lastActiveIndex !== -1 && this.tokenSpans[this.lastActiveIndex]) {
      this.tokenSpans[this.lastActiveIndex].classList.remove('highlighted');
    }
    this.lastActiveIndex = -1;
    this.isTokenized = false;
    this.tokenSpans = [];
  }
}
